/**
 * SessionsPage: Sessions-Verwaltung.
 *
 * Liste mit Filter (Status, Standort, Zeitraum), Edit-Drawer mit:
 *   - Stamm-Daten (Titel, Zeiten, Status, Teilnehmerzahl, Notizen)
 *   - Raum-Zuordnung
 *   - Trainer-Zuordnungen (Multi-Select)
 *   - Resource-Zuordnungen (Multi-Select)
 *
 * EXCLUDE-Constraint-Fehler werden vom Backend als 409 zurückgegeben und
 * inline angezeigt.
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  api,
  ApiCallError,
  type CourseSession,
  type CourseSessionWithRelations,
  type Location,
  type Room,
  type Resource,
  type Trainer,
  type SessionStatus,
} from "../lib/api";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Label } from "../components/Label";
import { Drawer } from "../components/Drawer";
import { SessionStatusBadge, SESSION_STATUS_LABEL } from "../components/Badge";
import { ListToolbar } from "../components/ListToolbar";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { AvailabilitySelect } from "../components/AvailabilitySelect";
import { SessionAvailabilityHint, useAvailabilityMap } from "../components/SessionAvailabilityHint";
import { useAuth } from "../lib/auth";
import { t } from "../locales/de";
import { shouldOpenSessionDeepLink } from "../lib/sessionNavigation";
import { matchesListSearch } from "../lib/listFiltering";

const STATUSES: SessionStatus[] = ["entwurf", "geplant", "abgeschlossen", "storniert"];

type FormState = {
  title: string;
  courseCode: string;
  locationId: string;
  plannedStart: string; // datetime-local: YYYY-MM-DDTHH:mm
  plannedEnd: string;
  participantCount: string;
  notes: string;
  status: SessionStatus;
  roomId: string;
  trainerIds: number[];
  resourceIds: number[];
};

type SeriesFormState = {
  title: string;
  courseCode: string;
  locationId: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  weekdays: number[];
  participantCount: string;
  notes: string;
  roomId: string;
  trainerIds: number[];
  resourceIds: number[];
};

const WEEKDAYS = [
  { value: 1, label: "Mo" }, { value: 2, label: "Di" }, { value: 3, label: "Mi" },
  { value: 4, label: "Do" }, { value: 5, label: "Fr" }, { value: 6, label: "Sa" },
  { value: 0, label: "So" },
];

function emptySeriesForm(locations: Location[]): SeriesFormState {
  return {
    title: "", courseCode: "", locationId: locations[0]?.id?.toString() ?? "",
    startDate: "", endDate: "", startTime: "", endTime: "", weekdays: [],
    participantCount: "0", notes: "", roomId: "", trainerIds: [], resourceIds: [],
  };
}

function previewSeriesDates({ startDate, endDate, weekdays }: SeriesFormState): Date[] {
  if (!startDate || !endDate || weekdays.length === 0 || startDate > endDate) return [];
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  if (Number.isNaN(start.valueOf()) || Number.isNaN(end.valueOf())) return [];
  const selected = new Set(weekdays);
  const dates: Date[] = [];
  for (const date = new Date(start); date <= end; date.setUTCDate(date.getUTCDate() + 1)) {
    if (selected.has(date.getUTCDay())) dates.push(new Date(date));
  }
  return dates;
}

function emptyForm(locations: Location[]): FormState {
  return {
    title: "",
    courseCode: "",
    locationId: locations[0]?.id?.toString() ?? "",
    plannedStart: "",
    plannedEnd: "",
    participantCount: "0",
    notes: "",
    status: "entwurf",
    roomId: "",
    trainerIds: [],
    resourceIds: [],
  };
}

function toLocalInput(iso: string): string {
  // datetime-local braucht YYYY-MM-DDTHH:mm in lokaler Zeit
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(local: string): string {
  // datetime-local → ISO (lokal → UTC)
  return new Date(local).toISOString();
}

function formatRange(s: string, e: string): string {
  const ds = new Date(s);
  const de = new Date(e);
  const dateStr = ds.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  const timeStr = (d: Date) => d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  return `${dateStr}, ${timeStr(ds)}–${timeStr(de)}`;
}

export function SessionsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { session: deepLinkedSessionId } = useSearch({ from: "/protected/sessions" });
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterLoc, setFilterLoc] = useState<string>("");
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [suppressedDeepLinkId, setSuppressedDeepLinkId] = useState<number | null>(null);
  const [seriesDrawerOpen, setSeriesDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<CourseSession | null>(null);
  const [form, setForm] = useState<FormState>({ title: "", courseCode: "", locationId: "", plannedStart: "", plannedEnd: "", participantCount: "0", notes: "", status: "entwurf", roomId: "", trainerIds: [], resourceIds: [] });
  const [seriesForm, setSeriesForm] = useState<SeriesFormState>(emptySeriesForm([]));
  const [error, setError] = useState<string | null>(null);
  const [seriesError, setSeriesError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [full, setFull] = useState<CourseSessionWithRelations | null>(null);

  const canWrite = user?.role === "admin" || user?.role === "planer";
  const canDelete = user?.role === "admin";

  const locQ = useQuery({ queryKey: ["locations"], queryFn: () => api.listLocations() });
  const locations = locQ.data?.locations ?? [];

  const sessQ = useQuery({
    queryKey: ["sessions", filterStatus, filterLoc],
    queryFn: () => api.listSessions({ status: filterStatus || undefined, locationId: filterLoc ? Number(filterLoc) : undefined }),
  });
  const sessions = sessQ.data?.sessions ?? [];

  // Daten für Edit-Drawer (Räume/Trainer/Resources des gewählten Standorts)
  const roomsQ = useQuery({
    queryKey: ["rooms", form.locationId],
    queryFn: () => api.listRooms(form.locationId ? Number(form.locationId) : undefined),
  });
  const trainersQ = useQuery({ queryKey: ["trainers"], queryFn: () => api.listTrainers() });
  const resourcesQ = useQuery({
    queryKey: ["resources", form.locationId],
    queryFn: () => api.listResources({ locationId: form.locationId ? Number(form.locationId) : undefined }),
  });
  const rooms: Room[] = roomsQ.data?.rooms ?? [];
  const seriesRoomsQ = useQuery({
    queryKey: ["rooms", "series", seriesForm.locationId],
    queryFn: () => api.listRooms(seriesForm.locationId ? Number(seriesForm.locationId) : undefined),
  });
  const seriesRooms = seriesRoomsQ.data?.rooms ?? [];
  const seriesResourcesQ = useQuery({
    queryKey: ["resources", "series", seriesForm.locationId],
    queryFn: () => api.listResources({ locationId: seriesForm.locationId ? Number(seriesForm.locationId) : undefined }),
  });
  const seriesResources: Resource[] = seriesResourcesQ.data?.resources ?? [];
  const trainers: Trainer[] = trainersQ.data?.trainers ?? [];
  const resources: Resource[] = resourcesQ.data?.resources ?? [];
  const selectableLocations = locations.filter(
    (location) => location.isActive || location.id === Number(form.locationId)
  );
  const activeLocations = locations.filter((location) => location.isActive);

  const createMut = useMutation({
    mutationFn: (body: Partial<CourseSession>) => api.createSession(body),
  });
  const createSeriesMut = useMutation({
    mutationFn: api.createSessionSeries,
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<CourseSession> }) => api.updateSession(id, body),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => api.deleteSession(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
    onError: (e) => alert(e instanceof ApiCallError ? e.message : "Löschen fehlgeschlagen"),
  });
  const deleteSeriesMut = useMutation({
    mutationFn: (seriesId: string) => api.deleteSessionSeries(seriesId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["sessions"] });
      setDrawerOpen(false);
    },
    onError: (e) => alert(e instanceof ApiCallError ? e.message : "Serie konnte nicht gelöscht werden"),
  });

  // Beim Öffnen des Drawers: volle Session laden
  useEffect(() => {
    if (!drawerOpen || !editing) {
      setFull(null);
      return;
    }
    api.getSession(editing.id).then((r) => setFull(r.session)).catch(() => setFull(null));
  }, [drawerOpen, editing]);

  function openCreate() {
    setEditing(null);
    setForm({ ...emptyForm(locations.filter((location) => location.isActive)), plannedStart: "", plannedEnd: "" });
    setError(null);
    setDrawerOpen(true);
  }
  function openSeriesCreate() {
    setSeriesForm(emptySeriesForm(activeLocations));
    setSeriesError(null);
    setSeriesDrawerOpen(true);
  }
  function submitSeries(e: React.FormEvent) {
    e.preventDefault();
    setSeriesError(null);
    const dates = previewSeriesDates(seriesForm);
    if (!seriesForm.title.trim()) return setSeriesError("Titel ist erforderlich");
    if (!seriesForm.locationId) return setSeriesError("Standort ist erforderlich");
    if (!seriesForm.startDate || !seriesForm.endDate || !seriesForm.startTime || !seriesForm.endTime) {
      return setSeriesError("Start- und Enddatum sowie Start- und Endzeit sind erforderlich");
    }
    if (seriesForm.startDate > seriesForm.endDate) return setSeriesError("Enddatum muss nach dem Startdatum liegen");
    if (seriesForm.startTime >= seriesForm.endTime) return setSeriesError("Endzeit muss nach Startzeit liegen");
    if (seriesForm.weekdays.length === 0) return setSeriesError("Mindestens ein Wochentag ist erforderlich");
    if (dates.length === 0) return setSeriesError("Im gewählten Zeitraum liegen keine Termine");

    void createSeriesMut.mutateAsync({
      title: seriesForm.title.trim(),
      courseCode: seriesForm.courseCode.trim() || undefined,
      locationId: Number(seriesForm.locationId),
      participantCount: Number(seriesForm.participantCount) || 0,
      notes: seriesForm.notes.trim() || undefined,
      weekdays: seriesForm.weekdays,
      startDate: seriesForm.startDate,
      endDate: seriesForm.endDate,
      startTime: seriesForm.startTime,
      endTime: seriesForm.endTime,
      roomId: seriesForm.roomId ? Number(seriesForm.roomId) : undefined,
      trainerIds: seriesForm.trainerIds,
      resourceIds: seriesForm.resourceIds,
    }).then(async () => {
      await qc.invalidateQueries({ queryKey: ["sessions"] });
      setSeriesDrawerOpen(false);
    }).catch((e: unknown) => {
      setSeriesError(e instanceof ApiCallError ? e.message : "Terminserie konnte nicht angelegt werden");
    });
  }
  function openEdit(s: CourseSession) {
    setEditing(s);
    setForm({
      title: s.title,
      courseCode: s.courseCode ?? "",
      locationId: s.locationId.toString(),
      plannedStart: toLocalInput(s.plannedStart),
      plannedEnd: toLocalInput(s.plannedEnd),
      participantCount: s.participantCount.toString(),
      notes: s.notes ?? "",
      status: s.status,
      roomId: "",
      trainerIds: [],
      resourceIds: [],
    });
    setError(null);
    setDrawerOpen(true);
  }

  function closeSessionDrawer() {
    if (deepLinkedSessionId) {
      setSuppressedDeepLinkId(deepLinkedSessionId);
    }
    setDrawerOpen(false);
    if (deepLinkedSessionId) void navigate({ to: "/sessions", search: {} });
  }

  // Dashboard-Links öffnen die bestehende Bearbeitungsoberfläche direkt.
  useEffect(() => {
    if (!deepLinkedSessionId) return;
    if (!canWrite) {
      void navigate({ to: "/sessions", search: {} });
      return;
    }
    if (!shouldOpenSessionDeepLink({ canWrite, sessionId: deepLinkedSessionId, suppressedSessionId: suppressedDeepLinkId, drawerOpen, editingId: editing?.id ?? null }) || sessQ.isLoading) return;
    const requestedSession = sessions.find((session) => session.id === deepLinkedSessionId);
    if (requestedSession) openEdit(requestedSession);
  }, [canWrite, deepLinkedSessionId, suppressedDeepLinkId, sessQ.isLoading, sessions, drawerOpen, editing?.id, navigate]);

  useEffect(() => {
    if (!deepLinkedSessionId && suppressedDeepLinkId !== null) setSuppressedDeepLinkId(null);
  }, [deepLinkedSessionId, suppressedDeepLinkId]);

  // Sobald `full` geladen ist, die IDs in die Form übertragen
  useEffect(() => {
    if (full && editing) {
      setForm((f) => ({
        ...f,
        roomId: full.room?.id?.toString() ?? "",
        trainerIds: full.trainers.map((t) => t.id),
        resourceIds: full.resources.map((r) => r.id),
      }));
    }
  }, [full, editing]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.title.trim()) return setError("Titel ist erforderlich");
    if (!form.locationId) return setError("Standort ist erforderlich");
    if (!form.plannedStart || !form.plannedEnd) return setError("Start- und Endzeit sind erforderlich");
    if (new Date(form.plannedStart) >= new Date(form.plannedEnd)) return setError("Endzeit muss nach Startzeit liegen");
    if (editing && !full) return setError("Sessiondaten werden noch geladen");
    const base = {
      title: form.title.trim(),
      courseCode: form.courseCode.trim() || undefined,
      locationId: Number(form.locationId),
      plannedStart: fromLocalInput(form.plannedStart),
      plannedEnd: fromLocalInput(form.plannedEnd),
      participantCount: Number(form.participantCount) || 0,
      notes: form.notes.trim() || undefined,
      status: form.status,
    };

    async function run() {
      setSaving(true);
      try {
        if (!editing) {
          // Neu anlegen
          const created = await createMut.mutateAsync(base);
          // Raum, Trainer, Ressourcen direkt zuordnen
          const id = created.session.id;
          if (form.roomId) await api.assignSessionRoom(id, Number(form.roomId));
          if (form.trainerIds.length) await api.assignSessionTrainers(id, form.trainerIds, true);
          if (form.resourceIds.length) await api.assignSessionResources(id, form.resourceIds);
        } else {
          const roomId = form.roomId ? Number(form.roomId) : null;
          const locationChanged = Number(form.locationId) !== full!.locationId;
          const roomChanged = roomId !== (full?.room?.id ?? null);
          if (locationChanged || roomChanged) {
            await api.moveSession(editing.id, { locationId: Number(form.locationId), roomId });
          }
          const { locationId: _locationId, ...sessionUpdate } = base;
          await updateMut.mutateAsync({ id: editing.id, body: sessionUpdate });
          if (form.trainerIds.length || (full?.trainers.length ?? 0) > 0) {
            await api.assignSessionTrainers(editing.id, form.trainerIds, true);
          }
          if (form.resourceIds.length || (full?.resources.length ?? 0) > 0) {
            await api.assignSessionResources(editing.id, form.resourceIds);
          }
        }
        await qc.invalidateQueries({ queryKey: ["sessions"] });
        closeSessionDrawer();
      } catch (e) {
        setError(e instanceof ApiCallError ? e.message : "Fehler beim Speichern");
      } finally {
        setSaving(false);
      }
    }

    void run();
  }

  const locName = (id: number) => locations.find((l) => l.id === id)?.name ?? `Standort ${id}`;
  const visibleSessions = sessions.filter((session) => matchesListSearch(search, [session.title, session.courseCode, locName(session.locationId)]));
  const resetFilters = () => {
    setSearch("");
    setFilterStatus("");
    setFilterLoc("");
  };
  const submitting = saving || createMut.isPending || updateMut.isPending;
  const seriesSubmitting = createSeriesMut.isPending;
  const seriesPreviewDates = previewSeriesDates(seriesForm);
  const sessionAvailability = useAvailabilityMap([{ plannedStart: form.plannedStart, plannedEnd: form.plannedEnd }], editing?.id, fromLocalInput);
  const seriesAvailability = useAvailabilityMap(seriesPreviewDates.map((date) => {
    const day = date.toISOString().slice(0, 10);
    return { plannedStart: `${day}T${seriesForm.startTime}`, plannedEnd: `${day}T${seriesForm.endTime}` };
  }), undefined, fromLocalInput);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t.nav.sessions}</h1>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={openSeriesCreate}>+ Serien-Sessions</Button>
            <Button onClick={openCreate}>+ Neue Session</Button>
          </div>
        )}
      </div>

      <ListToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Titel, Kurs-Code oder Standort suchen…" resultCount={visibleSessions.length} hasActiveFilters={Boolean(search || filterStatus || filterLoc)} onReset={resetFilters}>
        <select aria-label="Nach Status filtern" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Alle Status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{SESSION_STATUS_LABEL[s]}</option>)}
        </select>
        <select aria-label="Nach Standort filtern" value={filterLoc} onChange={(e) => setFilterLoc(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Alle Standorte</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </ListToolbar>

      <div className="rounded-lg border border-slate-200 bg-white">
        {sessQ.isLoading ? (
          <div className="p-6 text-sm text-slate-500">Lade…</div>
        ) : visibleSessions.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Keine Sessions für die aktuelle Suche oder Filterauswahl.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Titel</th>
                <th className="px-4 py-3">Standort</th>
                <th className="px-4 py-3">Zeitraum</th>
                <th className="px-4 py-3">Teilnehmer</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {visibleSessions.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-900">{s.title}</td>
                  <td className="px-4 py-3 text-slate-600">{locName(s.locationId)}</td>
                  <td className="px-4 py-3 text-slate-600">{formatRange(s.plannedStart, s.plannedEnd)}</td>
                  <td className="px-4 py-3 text-slate-600">{s.participantCount}</td>
                  <td className="px-4 py-3"><SessionStatusBadge status={s.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {canWrite && <Button variant="secondary" onClick={() => openEdit(s)}>Bearbeiten</Button>}
                      {canDelete && (
                        <Button variant="danger" onClick={() => {
                          if (confirm(`Session "${s.title}" wirklich löschen?`)) deleteMut.mutate(s.id);
                        }}>Löschen</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer open={seriesDrawerOpen} onClose={() => setSeriesDrawerOpen(false)} title="Serien-Sessions anlegen" width="36rem">
        <form onSubmit={submitSeries} className="space-y-4">
          {seriesError && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{seriesError}</div>}
          <div>
            <Label htmlFor="series-title">Titel *</Label>
            <Input id="series-title" value={seriesForm.title} onChange={(e) => setSeriesForm({ ...seriesForm, title: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="series-courseCode">Kurs-Code</Label>
            <Input id="series-courseCode" value={seriesForm.courseCode} onChange={(e) => setSeriesForm({ ...seriesForm, courseCode: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="series-location">Standort *</Label>
            <select id="series-location" value={seriesForm.locationId} onChange={(e) => setSeriesForm({ ...seriesForm, locationId: e.target.value, roomId: "" })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" required>
              <option value="">— bitte wählen —</option>
              {activeLocations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="series-startDate">Startdatum *</Label><Input id="series-startDate" type="date" value={seriesForm.startDate} onChange={(e) => setSeriesForm({ ...seriesForm, startDate: e.target.value })} required /></div>
            <div><Label htmlFor="series-endDate">Enddatum *</Label><Input id="series-endDate" type="date" value={seriesForm.endDate} onChange={(e) => setSeriesForm({ ...seriesForm, endDate: e.target.value })} required /></div>
            <div><Label htmlFor="series-startTime">Startzeit *</Label><Input id="series-startTime" type="time" value={seriesForm.startTime} onChange={(e) => setSeriesForm({ ...seriesForm, startTime: e.target.value })} required /></div>
            <div><Label htmlFor="series-endTime">Endzeit *</Label><Input id="series-endTime" type="time" value={seriesForm.endTime} onChange={(e) => setSeriesForm({ ...seriesForm, endTime: e.target.value })} required /></div>
          </div>
          <fieldset>
            <Label>Wochentage *</Label>
            <div className="flex flex-wrap gap-3">
              {WEEKDAYS.map((day) => <label key={day.value} className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={seriesForm.weekdays.includes(day.value)} onChange={(e) => setSeriesForm((f) => ({ ...f, weekdays: e.target.checked ? [...f.weekdays, day.value] : f.weekdays.filter((value) => value !== day.value) }))} />{day.label}</label>)}
            </div>
          </fieldset>
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="series-cap">Teilnehmer</Label><Input id="series-cap" type="number" min="0" value={seriesForm.participantCount} onChange={(e) => setSeriesForm({ ...seriesForm, participantCount: e.target.value })} /></div>
            <div>
              <AvailabilitySelect id="series-room" label="Raum" value={seriesForm.roomId} onChange={(roomId) => setSeriesForm({ ...seriesForm, roomId })} placeholder="— keiner —" disabled={!seriesForm.locationId} conflictedIds={seriesAvailability.roomIds} options={seriesRooms.filter((room) => room.isActive).map((room) => ({ id: room.id, label: `${room.name} (Kap. ${room.capacity})` }))} />
            </div>
          </div>
          <MultiSelectDropdown
            id="series-trainerIds"
            label="Trainer"
            options={trainers.filter((trainer) => trainer.isActive || seriesForm.trainerIds.includes(trainer.id)).map((trainer) => ({ id: trainer.id, label: trainer.fullName, detail: trainer.specialties.join(", "), inactive: !trainer.isActive }))}
            selectedIds={seriesForm.trainerIds}
            onChange={(trainerIds) => setSeriesForm((current) => ({ ...current, trainerIds }))}
            emptyMessage="Keine Trainer verfügbar."
            searchPlaceholder="Name oder Fachgebiet suchen…"
            conflictedIds={seriesAvailability.trainerIds}
          />
          <MultiSelectDropdown
            id="series-resourceIds"
            label="Ressourcen"
            options={seriesResources.filter((resource) => resource.isActive || seriesForm.resourceIds.includes(resource.id)).map((resource) => ({ id: resource.id, label: resource.name, detail: resource.category ?? undefined, inactive: !resource.isActive }))}
            selectedIds={seriesForm.resourceIds}
            onChange={(resourceIds) => setSeriesForm((current) => ({ ...current, resourceIds }))}
            emptyMessage="Keine Ressourcen für diesen Standort."
            searchPlaceholder="Name oder Kategorie suchen…"
            conflictedIds={seriesAvailability.resourceIds}
          />
          <div className="space-y-2">
            {seriesPreviewDates.map((date) => {
              const day = date.toISOString().slice(0, 10);
              return (
                <SessionAvailabilityHint
                  key={day}
                  plannedStart={`${day}T${seriesForm.startTime}`}
                  plannedEnd={`${day}T${seriesForm.endTime}`}
                  roomIds={seriesForm.roomId ? [Number(seriesForm.roomId)] : []}
                  trainerIds={seriesForm.trainerIds}
                  resourceIds={seriesForm.resourceIds}
                  toIso={fromLocalInput}
                />
              );
            })}
          </div>
          <div><Label htmlFor="series-notes">Notizen</Label><Input id="series-notes" value={seriesForm.notes} onChange={(e) => setSeriesForm({ ...seriesForm, notes: e.target.value })} /></div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <span className="font-medium">Vorschau: {seriesPreviewDates.length} Termine</span>
            {seriesPreviewDates.length > 0 && <ul className="mt-1 list-inside list-disc">{seriesPreviewDates.slice(0, 6).map((date) => <li key={date.toISOString()}>{date.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" })}</li>)}</ul>}
            {seriesPreviewDates.length > 6 && <div className="mt-1 text-slate-500">… und {seriesPreviewDates.length - 6} weitere</div>}
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
            <Button variant="secondary" type="button" onClick={() => setSeriesDrawerOpen(false)}>Abbrechen</Button>
            <Button type="submit" disabled={seriesSubmitting}>{seriesSubmitting ? "Lege an…" : "Serie anlegen"}</Button>
          </div>
        </form>
      </Drawer>

      <Drawer open={drawerOpen} onClose={closeSessionDrawer} title={editing ? "Session bearbeiten" : "Neue Session"} width="36rem">
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
          <div>
            <Label htmlFor="title">Titel *</Label>
            <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="courseCode">Kurs-Code</Label>
            <Input id="courseCode" value={form.courseCode} onChange={(e) => setForm({ ...form, courseCode: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="locationId">Standort *</Label>
            <select id="locationId" value={form.locationId} onChange={(e) => setForm({ ...form, locationId: e.target.value, roomId: "", resourceIds: [] })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" required>
              <option value="">— bitte wählen —</option>
              {selectableLocations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.isActive ? "" : " (inaktiv)"}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start">Start *</Label>
              <Input id="start" type="datetime-local" value={form.plannedStart} onChange={(e) => setForm({ ...form, plannedStart: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="end">Ende *</Label>
              <Input id="end" type="datetime-local" value={form.plannedEnd} onChange={(e) => setForm({ ...form, plannedEnd: e.target.value })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cap">Teilnehmer</Label>
              <Input id="cap" type="number" min="0" value={form.participantCount} onChange={(e) => setForm({ ...form, participantCount: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <select id="status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as SessionStatus })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{SESSION_STATUS_LABEL[s]}</option>)}
              </select>
            </div>
          </div>
          <AvailabilitySelect id="roomId" label="Raum" value={form.roomId} onChange={(roomId) => setForm({ ...form, roomId })} placeholder="— keiner —" disabled={!form.locationId} conflictedIds={sessionAvailability.roomIds} options={rooms.filter((room) => room.isActive || room.id === Number(form.roomId)).map((room) => ({ id: room.id, label: `${room.name} (Kap. ${room.capacity})`, inactive: !room.isActive }))} />
          <MultiSelectDropdown
            id="trainerIds"
            label="Trainer"
            options={trainers.filter((trainer) => trainer.isActive || form.trainerIds.includes(trainer.id)).map((trainer) => ({ id: trainer.id, label: trainer.fullName, detail: trainer.specialties.join(", "), inactive: !trainer.isActive }))}
            selectedIds={form.trainerIds}
            onChange={(trainerIds) => setForm((current) => ({ ...current, trainerIds }))}
            emptyMessage="Keine Trainer verfügbar."
            searchPlaceholder="Name oder Fachgebiet suchen…"
            conflictedIds={sessionAvailability.trainerIds}
          />
          <MultiSelectDropdown
            id="resourceIds"
            label="Ressourcen"
            options={resources.filter((resource) => resource.isActive || form.resourceIds.includes(resource.id)).map((resource) => ({ id: resource.id, label: resource.name, detail: resource.category ?? undefined, inactive: !resource.isActive }))}
            selectedIds={form.resourceIds}
            onChange={(resourceIds) => setForm((current) => ({ ...current, resourceIds }))}
            emptyMessage="Keine Ressourcen für diesen Standort."
            searchPlaceholder="Name oder Kategorie suchen…"
            conflictedIds={sessionAvailability.resourceIds}
          />
          <SessionAvailabilityHint
            plannedStart={form.plannedStart}
            plannedEnd={form.plannedEnd}
            roomIds={form.roomId ? [Number(form.roomId)] : []}
            trainerIds={form.trainerIds}
            resourceIds={form.resourceIds}
            excludeSessionId={editing?.id}
            toIso={fromLocalInput}
          />
          <div>
            <Label htmlFor="notes">Notizen</Label>
            <Input id="notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
            {canDelete && editing?.seriesId && (
              <Button
                variant="danger"
                type="button"
                disabled={deleteSeriesMut.isPending}
                onClick={() => {
                  if (confirm("Gesamte Serie löschen? Alle zugehörigen Termine werden dauerhaft entfernt.")) {
                    deleteSeriesMut.mutate(editing.seriesId!);
                  }
                }}
              >
                {deleteSeriesMut.isPending ? "Lösche Serie…" : "Gesamte Serie löschen"}
              </Button>
            )}
            <Button variant="secondary" type="button" onClick={closeSessionDrawer}>Abbrechen</Button>
            <Button type="submit" disabled={submitting}>{submitting ? "Speichere…" : editing ? "Speichern" : "Anlegen"}</Button>
          </div>
        </form>
      </Drawer>
    </div>
  );
}
